import SignaturePad from 'signature_pad';

export interface SignatureFieldPreparerOptions {
    onBlur?: (element: HTMLElement) => void;
    onChange?: (element: HTMLElement) => void;
}

export class SignatureFieldPreparer {
    private signaturePad: SignaturePad | null = null;
    private resizeObserver: ResizeObserver | null = null;
    private predefinedDataUrl: string | null = null;

    constructor(
        private readonly fieldElement: HTMLElement,
        private readonly options: SignatureFieldPreparerOptions = {}
    ) {}

    public get element(): HTMLElement {
        return this.fieldElement;
    }

    public prepare(): void {
        const canvas = this.fieldElement.querySelector<HTMLCanvasElement>('canvas');
        const overlay = this.fieldElement.querySelector<HTMLElement>('.overlay');
        const clearButton = this.fieldElement.querySelector<HTMLElement>('.signature-clear');
        const fileInput = this.fieldElement.querySelector<HTMLInputElement>('.input');

        if (!canvas || !fileInput) {
            return;
        }

        const setOverlayVisible = (visible: boolean): void => {
            if (overlay) {
                overlay.style.display = visible ? '' : 'none';
            }
        };

        const updateOverlay = (): void => {
            setOverlayVisible(this.signaturePad?.isEmpty() ?? true);
        };

        const clearFileInput = (): void => {
            fileInput.files = new DataTransfer().files;
        };

        const resizeCanvas = (): void => {
            if (!this.signaturePad) {
                return;
            }

            const width = canvas.offsetWidth;
            const height = canvas.offsetHeight;

            if (!width || !height) {
                return;
            }

            const data = this.signaturePad?.toData();

            const ratio = Math.max(window.devicePixelRatio || 1, 1);
            canvas.width = width * ratio;
            canvas.height = height * ratio;

            canvas.getContext('2d')?.scale(ratio, ratio);

            this.signaturePad?.clear();
            if (data && data.length > 0) {
                this.signaturePad?.fromData(data);
            } else if (this.predefinedDataUrl) {
                this.signaturePad?.fromDataURL(this.predefinedDataUrl);
            }
        };

        const emitChangeEvent = (): void => {
            this.options.onChange?.(this.fieldElement);
            if (this.options.onBlur) {
                this.options.onBlur(this.fieldElement);
            }
            updateOverlay();
        };

        const updateFileInput = (): void => {
            if (!this.signaturePad) {
                return;
            }

            if (this.signaturePad.isEmpty()) {
                clearFileInput();
                emitChangeEvent();
                return;
            }

            const dataUrl = this.signaturePad.toDataURL('image/png');
            const file = this.dataUrlToFile(dataUrl, 'signature.png');

            const dt = new DataTransfer();
            dt.items.add(file);
            fileInput.files = dt.files;

            emitChangeEvent();
        };

        this.signaturePad = new SignaturePad(canvas);

        this.resizeObserver = new ResizeObserver(() => {
            resizeCanvas();
        });

        this.resizeObserver.observe(canvas);

        window.addEventListener('resize', resizeCanvas);

        this.signaturePad.addEventListener('beginStroke', () => {
            if (this.predefinedDataUrl) {
                this.predefinedDataUrl = null;
                canvas.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height);
            }
            setOverlayVisible(false);
        });
        this.signaturePad.addEventListener('endStroke', () => updateFileInput());

        clearButton?.addEventListener('click', event => {
            event.preventDefault();
            this.predefinedDataUrl = null;
            this.signaturePad?.clear();
            clearFileInput();
            emitChangeEvent();
        });
    }

    public async loadFromDataURL(dataUrl: string): Promise<void> {
        if (!this.signaturePad) {
            return;
        }

        this.predefinedDataUrl = dataUrl;
        await this.signaturePad.fromDataURL(dataUrl);

        const overlay = this.fieldElement.querySelector<HTMLElement>('.overlay');
        if (overlay) {
            overlay.style.display = 'none';
        }

        const fileInput = this.fieldElement.querySelector<HTMLInputElement>('.input');
        if (fileInput) {
            const file = this.dataUrlToFile(dataUrl, 'signature.png');
            const dt = new DataTransfer();
            dt.items.add(file);
            fileInput.files = dt.files;
        }

        this.options.onChange?.(this.fieldElement);
    }

    private dataUrlToFile(dataUrl: string, fileName: string): File {
        const [meta, base64] = dataUrl.split(',');
        const mime = meta.match(/:(.*?);/)?.[1] ?? 'image/png';

        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }

        return new File([bytes], fileName, { type: mime });
    }

    public destroy(): void {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
        if (this.signaturePad) {
            this.signaturePad = null;
        }
    }
}

/**
 * Initialize signature fields in a container element
 */
export function initializeSignatureFields(container: HTMLElement, options: SignatureFieldPreparerOptions = {}): SignatureFieldPreparer[] {
    const signatureFields = container.querySelectorAll<HTMLElement>('[data-element="field"][data-field="signature"]');
    const preparers: SignatureFieldPreparer[] = [];

    signatureFields.forEach(field => {
        const preparer = new SignatureFieldPreparer(field, options);
        preparer.prepare();
        preparers.push(preparer);
    });

    return preparers;
}
